import React from 'react';
import { cn } from './cn';

/**
 * Primitivas de tabla tipo "ledger": filetes (hairlines) en lugar de bordes pesados,
 * encabezados en salvia y scroll horizontal nativo. Las páginas componen filas con
 * <TH>/<TD> y mantienen su lógica de datos.
 */

export interface TableProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Altura máxima antes de hacer scroll vertical (ej: '60vh'). */
  maxHeight?: string;
}

export const Table: React.FC<TableProps> = ({ maxHeight, className, children, ...rest }) => (
  <div
    className={cn('w-full overflow-x-auto', className)}
    style={maxHeight ? { maxHeight } : undefined}
    {...rest}
  >
    <table className="w-full border-collapse text-sm">{children}</table>
  </div>
);

export const THead: React.FC<React.HTMLAttributes<HTMLTableSectionElement>> = ({ className, children, ...rest }) => (
  <thead className={cn(className)} {...rest}>
    {children}
  </thead>
);

export const TBody: React.FC<React.HTMLAttributes<HTMLTableSectionElement>> = ({ className, children, ...rest }) => (
  <tbody className={cn('divide-y divide-hairline/70', className)} {...rest}>
    {children}
  </tbody>
);

export interface THProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  sticky?: boolean;
}

export const TH: React.FC<THProps> = ({ sticky, className, children, ...rest }) => (
  <th
    className={cn(
      'whitespace-nowrap border-b border-hairline bg-paper-2/70 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-sage',
      sticky && 'sticky top-0 z-10',
      className,
    )}
    {...rest}
  >
    {children}
  </th>
);

export const TD: React.FC<React.TdHTMLAttributes<HTMLTableCellElement>> = ({ className, children, ...rest }) => (
  <td className={cn('px-3 py-2.5 align-middle text-ink', className)} {...rest}>
    {children}
  </td>
);

export interface TRProps extends React.HTMLAttributes<HTMLTableRowElement> {
  selectable?: boolean;
}

export const TR: React.FC<TRProps> = ({ selectable, className, children, ...rest }) => (
  <tr className={cn(selectable && 'cursor-pointer transition-colors hover:bg-paper-2/50', className)} {...rest}>
    {children}
  </tr>
);

export default Table;
